import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { Event } from '../../types';
import { useLang } from '../../i18n/context';

export function DiscoveryTab() {
  const { t, lang } = useLang();
  const [recentLogs, setRecentLogs] = useState<any[]>([]);
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'events' | 'logs'>('events');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [logs, events] = await Promise.all([
        api.getScrapeLogs({ limit: '30' }),
        api.getEvents({ status: 'validated', limit: '100' }),
      ]);
      setRecentLogs(logs);
      // Sort: upcoming events first (closest date on top), then past events (most recent on top)
      const now = new Date().toISOString().split('T')[0];
      const upcoming = events.filter((e: any) => e.date >= now).sort((a: any, b: any) => a.date.localeCompare(b.date));
      const past = events.filter((e: any) => e.date < now).sort((a: any, b: any) => b.date.localeCompare(a.date));
      const sorted = [...upcoming, ...past];
      setRecentEvents(sorted);
    } catch (err) {
      console.error('Failed to load discovery data:', err);
    } finally {
      setLoading(false);
    }
  }

  const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-BE';

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const formatEventDate = (d: string) =>
    new Date(d).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });

  const isPast = (d: string) => new Date(d) < new Date();

  // Count events by source
  const totalScrapedEvents = recentLogs.reduce((sum: number, l: any) => sum + (l.events_found || 0), 0);
  const successLogs = recentLogs.filter((l: any) => l.status === 'success');
  const errorLogs = recentLogs.filter((l: any) => l.status === 'error');

  return (
    <div>
      <div className="section-header">
        <span className="section-title">{t('discovery.title')}</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className={`filter-chip ${view === 'events' ? 'active' : ''}`} onClick={() => setView('events')}>
            {t('discovery.events_discovered')} ({recentEvents.length})
          </button>
          <button className={`filter-chip ${view === 'logs' ? 'active' : ''}`} onClick={() => setView('logs')}>
            {t('discovery.scan_logs')} ({recentLogs.length})
          </button>
          <button className="btn btn-primary" onClick={loadAll}>&#8635; {t('discovery.refresh')}</button>
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontFamily: 'JetBrains Mono', fontWeight: 700, color: 'var(--green)' }}>{recentEvents.length}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('discovery.events_label')}</div>
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontFamily: 'JetBrains Mono', fontWeight: 700, color: 'var(--gold)' }}>{successLogs.length}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('discovery.scans_ok')}</div>
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontFamily: 'JetBrains Mono', fontWeight: 700, color: errorLogs.length > 0 ? 'var(--red)' : 'var(--text-muted)' }}>{errorLogs.length}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('discovery.errors')}</div>
        </div>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{totalScrapedEvents}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{t('discovery.detected_by_scan')}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>
      ) : view === 'events' ? (
        /* ===== EVENTS VIEW ===== */
        recentEvents.length === 0 ? (
          <div className="empty-state"><p>{t('discovery.no_events')}</p></div>
        ) : (
          <div>
            {recentEvents.map((event: any) => {
              const past = isPast(event.date);
              return (
                <div key={event.id} style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-color)',
                  borderLeft: `3px solid ${past ? 'var(--text-muted)' : 'var(--green)'}`,
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '8px',
                  opacity: past ? 0.6 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    {/* Thumbnail */}
                    {(event.image_url || event.flyer_url) && (
                      <div style={{
                        width: '80px', height: '80px', flexShrink: 0, marginRight: '12px',
                        borderRadius: '6px', overflow: 'hidden', background: 'rgba(255,255,255,0.05)',
                      }}>
                        <img
                          src={event.flyer_url || event.image_url}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          loading="lazy"
                          onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{
                          fontFamily: 'JetBrains Mono', fontSize: '11px', fontWeight: 600,
                          color: past ? 'var(--text-muted)' : 'var(--gold)',
                        }}>
                          {formatEventDate(event.date)}
                        </span>
                        {!past && (
                          <span style={{
                            padding: '1px 6px', borderRadius: '3px', fontSize: '9px', fontWeight: 700,
                            background: 'rgba(76,175,80,0.15)', color: 'var(--green)', textTransform: 'uppercase',
                          }}>{t('agenda.upcoming')}</span>
                        )}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>{event.title}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {(event.venue_name || event.venue_address) && <span>&#128205; {event.venue_name || event.venue_address}</span>}
                        {(event.city || event.venue_city) && <span>&#127961; {event.city || event.venue_city}</span>}
                        {event.is_free ? <span style={{ color: 'var(--green)' }}>&#10003; {t('common.free')}</span> :
                          event.price_info && <span>&#127915; {event.price_info}</span>}
                      </div>
                      {event.genre_tags?.length > 0 && (
                        <div style={{ marginTop: '6px' }} className="genre-tags">
                          {event.genre_tags.map((g: string) => (
                            <span key={g} className={`genre-tag ${g.replace(/\s+/g, '-')}`}>{g}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                      {event.source_url && (
                        <a href={event.source_url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: '11px', color: 'var(--gold)', textDecoration: 'none' }}>
                          &#128279; {t('events.source')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* ===== LOGS VIEW ===== */
        recentLogs.length === 0 ? (
          <div className="empty-state"><p>{t('discovery.no_scans')}</p></div>
        ) : (
          <div>
            {recentLogs.map((log: any) => (
              <div key={log.id} style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{log.source_name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatDate(log.started_at)}</div>
                  {log.errors && (
                    <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '4px' }}>
                      {(() => { try { return JSON.parse(log.errors).join('; '); } catch { return log.errors; } })()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    fontFamily: 'JetBrains Mono', fontSize: '13px',
                    color: log.events_found > 0 ? 'var(--green)' : 'var(--text-muted)',
                  }}>
                    {log.events_found} events
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    textTransform: 'uppercase' as const,
                    background: log.status === 'success'
                      ? (log.events_found > 0 ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)')
                      : log.status === 'error' ? 'rgba(229,57,53,0.15)' : 'rgba(255,152,0,0.15)',
                    color: log.status === 'success'
                      ? (log.events_found > 0 ? 'var(--green)' : 'var(--orange)')
                      : log.status === 'error' ? 'var(--red)' : 'var(--orange)',
                  }}>
                    {log.status === 'success' && log.events_found === 0 ? 'NO DATA' : log.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
